import React, { useState, useMemo, FunctionComponent, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';
import { ExpiredOverlay, CartStatusEnum } from '@pegasus/cart';
import { LanguageCode, TranslateScope, useTranslationContext } from '@pegasus/translation';
import { Header, GuestHeader } from '@pegasus/ui-headers';
import { useIsEligibleForVoucher } from '@pegasus/platform-journeys';
import { useConfig } from '@browsing/config';

import { isMoreThanDaysAgo, isUpcomingEnabled } from '@client/helpers';
import { useZalandoPlus } from '@client/components/mylounge/plus';
import { useCart } from '@client/lib/cart/hooks';
import { getWebviewOs } from '@client/redux/modules/clientDevice/selectors';
import { getToken } from '@client/redux/modules/user/selectors';
import {
    getHeaderNotificationBanner,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import { getEmail, getFirstName } from '@client/redux/modules/customer';
import { UspSlider } from '@client/components/mylounge/usp/uspSlidder';
import { trackEvents, M1_REBRANDING } from '@client/components/mylounge/constants';
import useCustomCommercialMessage from '@client/components/mylounge/banner/use-commercial-message';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { CommercialMessage } from '@pegasus/commercial-messages';
import { track } from '@client/helpers/tracking';
import { getCookie } from '@client/helpers/cookie';
import { screenSizes } from '@lux/tokens';
import { useWindowWidth } from '@lux/hooks';
import SmartBanner from '@client/components/shared/SmartBanner';
import { USP_VISIBILITY_START_DATE } from '@client/components/constants';
import { isSmartBannerClosed } from '@pegasus/smartbanner';
import { useConsentManager } from '@pegasus/tracker';
import { useSubscribeToNewsletter, useIsPlatformJourney } from '../mylounge/platform-journeys';
import { getSupportedLanguages } from './helpers';
import { Timer } from './timer';
import FlashMessages from './flash-messages';
import Notifications from './notifications';
import { TabsWrapper } from '../mylounge/banner/Tabs/TabsWrapper';
import {
    HeaderWrap,
    TabsContainer,
    HeaderRowsHolder,
    HeaderBannerRow,
} from './headerWrapper.styles';

type Props = {
    isGuest?: boolean;
    updateApplicationContext?: (isSmartbannerShown: boolean) => void;
};

const LoadableDatePicker = loadable(
    () => import(/* webpackChunkName: "date-picker" */ './date-picker'),
);

export const HeaderWrapper: FunctionComponent<Props> = ({
    isGuest = false,
    updateApplicationContext,
}) => {
    const pathName = window.location.pathname;
    const dispatch = useDispatch();
    const email = useSelector(getEmail);
    const firstName = useSelector(getFirstName);
    const isPlatformJourneyEnabled = useIsPlatformJourney();
    const isEligibleForVoucher = useIsEligibleForVoucher();
    const isWebview = !!useSelector(getWebviewOs);
    const showTabsAndCommercialMsg = pathName.startsWith('/event');
    const config = useConfig();
    const isDoi = config.isEnabled('doi');
    const { translations } = useTranslationContext();
    const [timerState, setTimerState] = useState<CartStatusEnum>(null);
    const isM1Rebranding = config.isEnabled(M1_REBRANDING);
    const commercialMessage = useCustomCommercialMessage();
    const [hasConsent] = useConsentManager('smart_app_download_banner');

    // to show component on mylounge and success page only - Will reuse this code.
    // const isEventPage: boolean = pathName.includes('event');
    // const isEventOrSuccess: boolean = isEventPage || isOrderSuccessPage;
    const isOrderSuccessPage: boolean = pathName.includes('success');
    const bannerContext = isOrderSuccessPage ? 'ordersuccess' : 'mylounge';

    const [isBannerShown, setIsBannerShown] = useState(true);
    const [isBannerShouldBeRemovedInDom, setIsBannerShouldBeRemovedInDom] = useState(() => {
        return isSmartBannerClosed(bannerContext) || !isOrderSuccessPage;
    });
    let isConsentGiven = hasConsent;
    isConsentGiven = isConsentGiven || !!getCookie('smartbanner_consent');

    const screenWidth = useWindowWidth();
    const isMobileView = screenWidth < screenSizes.sm;

    const [{ cart, recoveredCart }, cartActions] = useCart();
    const cartInfo = cart && {
        savings: cart.savings,
        total: cart.total,
        shippingCosts: cart.shippingCosts,
    };
    const cartItems = (cart && cart.items) || [];
    const recoveredItems = (recoveredCart && recoveredCart.items) || [];
    const csrfToken = useSelector(getToken);
    const headerNotificationBanner = useSelector(getHeaderNotificationBanner);
    const subscribeToNewsletter = useSubscribeToNewsletter();

    const { isEnabled: isPlusEnabled } = useZalandoPlus();
    const supportedLanguages = useMemo(() => getSupportedLanguages(config), [config]);

    const extendCart = useCallback(() => {
        cartActions.onExtendCart();
        setTimerState(null);
    }, [cartActions]);

    const renderTimer = useCallback(
        () => (
            <TranslateScope path="pushNotification">
                <Timer
                    setTimerState={setTimerState}
                    onExpiredCart={cartActions.onExpiredCart}
                    cart={cart}
                />
            </TranslateScope>
        ),
        [cart, cartActions.onExpiredCart],
    );

    const handleComponentRemovalState = useCallback(() => {
        setIsBannerShown(false);
        setIsBannerShouldBeRemovedInDom(true);
    }, [setIsBannerShouldBeRemovedInDom, setIsBannerShown]);

    const handleListenToBannerPosition = useCallback((isShown: boolean) => {
        setIsBannerShown(isShown);
    }, []);

    const [showUSPVisibility] = useState(() =>
        isMoreThanDaysAgo(window.localStorage.getItem(USP_VISIBILITY_START_DATE)),
    );

    useEffect(() => {
        if (updateApplicationContext) {
            updateApplicationContext(!isBannerShouldBeRemovedInDom && isBannerShown);
        }
    }, [updateApplicationContext, isBannerShouldBeRemovedInDom, isBannerShown]);

    const getTopPosition = () => {
        if (isM1Rebranding) {
            if (!showUSPVisibility) {
                return 0;
            }

            if (isBannerShouldBeRemovedInDom) {
                return -32;
            }

            return isBannerShown ? 0 : -92;
        }
        return isBannerShouldBeRemovedInDom || isBannerShown ? 0 : -60;
    };

    if (isWebview) {
        return null;
    }

    // Fix the floating Menu
    return (
        <HeaderWrap movePostionTopBy={getTopPosition()}>
            <HeaderRowsHolder>
                {!isBannerShouldBeRemovedInDom && (
                    <HeaderBannerRow>
                        <SmartBanner
                            listenToBannerPosition={handleListenToBannerPosition}
                            updateComponentRemovalState={handleComponentRemovalState}
                            bannerContext={bannerContext}
                        />
                    </HeaderBannerRow>
                )}

                <UspSlider isM1Rebranding={isM1Rebranding} showUSPVisibility={showUSPVisibility} />

                <HeaderBannerRow height={isMobileView ? 60 : 75}>
                    {isGuest ? (
                        <GuestHeader
                            supportedLanguages={supportedLanguages}
                            isPrive={config.isPriveDomain}
                        />
                    ) : (
                        <Header
                            isAutoLoginEnabled={config.isEnabled('autoLoginTooltip')}
                            translations={translations}
                            languageCode={translations.locale as LanguageCode}
                            csrfToken={csrfToken}
                            timer={renderTimer}
                            cart={{
                                items: cartItems,
                                recoveredItems,
                                info: cartInfo,
                            }}
                            cartEvents={{
                                onItemRemove: cartActions.onItemRemove,
                                onItemReserveAgain: cartActions.onItemReserveAgain,
                            }}
                            datePicker={isUpcomingEnabled(config) ? <LoadableDatePicker /> : null}
                            supportedLanguages={supportedLanguages}
                            isPlusEnabled={isPlusEnabled}
                            isPrive={config.isPriveDomain}
                            isPlatformJourneyEnabled={isPlatformJourneyEnabled}
                            showSuccessMessage={(message: string) =>
                                dispatch(showHeaderSuccessFlashMessage(message))
                            }
                            isDOIUser={isDoi}
                            isSubscribedToNewsletter={!isEligibleForVoucher}
                            isMylounge
                            subscribeToNewsletter={subscribeToNewsletter}
                            email={email}
                            notification={headerNotificationBanner}
                            firstName={firstName}
                        />
                    )}
                </HeaderBannerRow>

                {!isM1Rebranding && (
                    <HeaderBannerRow height="auto">
                        <FlashMessages />
                        <Notifications />
                    </HeaderBannerRow>
                )}
            </HeaderRowsHolder>

            {isM1Rebranding && showTabsAndCommercialMsg && (
                <TranslateScope path="myLounge">
                    <TabsContainer>
                        <FlashMessages />
                        <Notifications />
                        <TabsWrapper />
                        {!isPlatformJourneyEnabled &&
                            commercialMessage?.isEnabled &&
                            !commercialMessage?.isModal && (
                                <TransitionGroup className="transition__group">
                                    {commercialMessage.isShown && (
                                        <CSSTransition
                                            classNames="commercial-transition"
                                            timeout={300}
                                        >
                                            <CommercialMessage
                                                event={commercialMessage.event}
                                                onRedeem={commercialMessage.onRedeem}
                                                onClose={commercialMessage.onClose}
                                                track={track}
                                                trackEvents={trackEvents}
                                            />
                                        </CSSTransition>
                                    )}
                                </TransitionGroup>
                            )}
                    </TabsContainer>
                </TranslateScope>
            )}

            <ExpiredOverlay
                translations={translations}
                isShown={!!timerState}
                status={timerState}
                closeDialog={() => {
                    setTimerState(null);
                }}
                extendCart={extendCart}
            />
        </HeaderWrap>
    );
};

export default HeaderWrapper;
